import type { BankPayment } from './bank-statement'

// Who each bank payment is from (plan: docs/plans/bank-statement-matching.md).
// Pure, so every rule is tested without a screen. Accuracy over reach: a
// payment is only "sure" when ONE customer fits the amount, the words on the
// payment point at them in a way that cannot mean someone else (a full name,
// a remembered payer name, their house and street, their postcode), and no
// other customer's name fits the payment as well. Anything less is shown to
// the owner to decide, and nothing is marked paid until the owner taps.
// Reviewed 2026-09-27 (scratchpad bank-review/REVIEW.md); the fixes are
// marked with the finding's number.

export interface OwedVisit {
  cleanId: number
  customerId: number
  date: string
  pence: number
  version: number
  // 'unpaid' = recorded as owed; 'unknown' = nobody recorded either way.
  status: 'unpaid' | 'unknown'
  chased: number
  // Several jobs (step 2): which of the customer's jobs, and its name for the owner to read. Optional: a round with
  // several jobs off sends neither, and every visit is the one job.
  jobId?: number | null
  job?: string | null
  // Money brought from Squeegee (0043): what the customer owes on an account, matched like a visit (bank-brought.ts).
  account?: boolean
}

// How the matching is asked to run. `severalJobs`: the round has several jobs on (PLAN 4), so a payment that fits
// more than one set of the customer's jobs (£60: the gutters, or three windows visits), or a customer owing more
// visits than the search covers, is never "sure": the owner chooses.
export interface MatchOptions {
  severalJobs?: boolean
  // Invoices to customers still to be paid (0065): a payment whose words hold one's number ("DWC-0014", "DWC 14") and
  // whose amount is what its unpaid visits still owe is sure; the number with another amount goes to the owner to check.
  invoices?: InvoiceRef[]
}
export interface InvoiceRef { number: string; customerId: number; cleanIds: number[] }

// The invoice a payment names, if any: its letters then its number, spaces, dashes and leading noughts allowed.
export function invoiceNamed(p: BankPayment, invoices: InvoiceRef[] = []): InvoiceRef | null {
  const text = `${p.who ?? ''} ${p.text}`.toUpperCase()
  for (const inv of invoices) {
    const m = /^([A-Z]{1,6})-0*(\d+)$/.exec(inv.number)
    if (m && new RegExp(`(^|[^A-Z0-9])${m[1]}[\\s-]*0*${m[2]}(?![0-9])`).test(text)) return inv
  }
  return null
}

// A visit already marked paid (the Paid or Bank button, or an earlier
// statement): what a payment on an older statement usually was for.
export interface PaidVisit {
  cleanId: number
  customerId: number
  date: string
  pence: number
  method: string | null
}

// A payment that fits visits already marked paid for the customer named on it
// (Liam 2026-09-28: "Should it say paid already instead of not matched?").
// Shown so the owner knows it is accounted for; nothing is changed.
export interface AlreadyPaidMatch {
  payment: BankPayment
  customerId: number
  name: string
  visits: PaidVisit[]
}

export interface PayerCustomer {
  id: number
  name: string
  address: string | null
  postcode: string | null
  // One per line, remembered from confirmed matches (0030).
  payer_names: string | null
}

export interface Candidate {
  customerId: number
  name: string
  // The visits this payment would settle, oldest first.
  visits: OwedVisit[]
  // Why Roundly thinks it is them: "full name", "surname", "remembered", "house and street", "postcode".
  reasons: string[]
  score: number
  // Evidence strong enough to call it sure without the owner (see top).
  sureable: boolean
  // The payer's name on this payment can be remembered for them (a full name
  // matched); otherwise only when the owner picks them (finding 10).
  rememberable: boolean
  // Several jobs: more than one set of their visits adds up to the payment (or more are owed than the search can
  // rule out). `alternatives` are the sets found; `owed` every visit of theirs the owner may tick instead.
  alternatives?: OwedVisit[][]
  owed?: OwedVisit[]
}

export interface PaymentMatch {
  payment: BankPayment
  // The bank payment's fingerprint, stored on each visit it pays (paid_ref).
  ref: string
  sure: Candidate | null
  // For the owner to choose from, best first (empty when sure).
  choices: Candidate[]
  // No amount fits, but the words point at a customer: shown, never acted on.
  hint: string | null
}

export interface MatchResult {
  sure: PaymentMatch[]
  check: PaymentMatch[]
  unmatched: PaymentMatch[]
  // Payments already used to tick visits off (uploaded before).
  alreadyUsed: number
  // Payments for visits already marked paid another way: nothing to do.
  alreadyPaid: AlreadyPaidMatch[]
}

// Words banks add that say nothing about who paid.
const NOISE = new Set([
  'FASTER', 'PAYMENT', 'PAYMENTS', 'FPI', 'FPO', 'FP', 'BGC', 'BAC', 'BACS', 'TFR', 'TRANSFER', 'TRF', 'CR', 'DR',
  'BANK', 'GIRO', 'CREDIT', 'FROM', 'TO', 'REF', 'REFERENCE', 'MOBILE', 'ONLINE', 'INTERNET', 'PAY', 'PAID', 'BILL',
  'STO', 'SO', 'DD', 'FT', 'MR', 'MRS', 'MS', 'MISS', 'THE', 'AND', 'FOR', 'VIA', 'WINDOW', 'WINDOWS',
  'CLEAN', 'CLEANING', 'CLEANER', 'CLEANERS', 'WC', 'GBP', 'RECEIVED', 'INWARD', 'OUTWARD', 'STANDING', 'ORDER',
  'MONZO', 'STARLING', 'REVOLUT', 'BARCLAYS', 'LLOYDS', 'HSBC', 'NATWEST', 'SANTANDER', 'NATIONWIDE', 'HALIFAX',
  'LTD', 'LIMITED', 'PLC', 'THANKS', 'THANK', 'YOU', 'INV', 'INVOICE', 'DEPOSIT', 'SENT',
])
// Payments through these are never one customer: never remembered as a
// payer (finding 10). Whole phrases, so a customer called Wise or Post is
// still remembered (round 2, finding 11).
const NOT_A_PAYER = [/\bPOST OFFICE\b/, /\bCASH (DEPOSIT|IN|PAID IN)\b/, /\bCOUNTER CREDIT\b/, /\bSTRIPE\b/, /\bSUMUP\b/, /\bPAYPAL\b/, /\bGOCARDLESS\b/, /\bI?ZETTLE\b/, /\bSQUARE (UK|INC|PAYMENTS)\b/, /\bHMRC\b/, /\bINTEREST\b/, /\bREFUND\b/, /\bCHEQUE\b/, /\bCHQ\b/, /\bATM\b/, /\bLOAN\b/]
const MONTHS = new Set(['JAN', 'JANUARY', 'FEB', 'FEBRUARY', 'MAR', 'MARCH', 'APR', 'APRIL', 'MAY', 'JUN', 'JUNE', 'JUL', 'JULY', 'AUG', 'AUGUST', 'SEP', 'SEPT', 'SEPTEMBER', 'OCT', 'OCTOBER', 'NOV', 'NOVEMBER', 'DEC', 'DECEMBER'])
const TITLES = new Set(['MR', 'MRS', 'MS', 'MISS', 'DR', 'SIR', 'LADY', 'PROF', 'MX'])
const STREET_TYPES: Record<string, string> = {
  ROAD: 'ROAD', RD: 'ROAD', LANE: 'LANE', LN: 'LANE', STREET: 'STREET', ST: 'STREET', AVENUE: 'AVENUE', AVE: 'AVENUE',
  CLOSE: 'CLOSE', CL: 'CLOSE', DRIVE: 'DRIVE', DR: 'DRIVE', WAY: 'WAY', GARDENS: 'GARDENS', GDNS: 'GARDENS',
  CRESCENT: 'CRESCENT', CRES: 'CRESCENT', PLACE: 'PLACE', PL: 'PLACE', COURT: 'COURT', CT: 'COURT', GROVE: 'GROVE',
  GR: 'GROVE', HILL: 'HILL', PARK: 'PARK', RISE: 'RISE', TERRACE: 'TERRACE', WALK: 'WALK', MEWS: 'MEWS', SQUARE: 'SQUARE',
}
const NOT_NAMES = new Set([...Object.keys(STREET_TYPES), 'FLAT', 'HOUSE', 'COTTAGE', 'THE'])

export function words(s: string): string[] {
  return s.toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim().split(' ').filter(Boolean)
}

// What the payment says, in order, without the bank's own words.
export function paymentWords(p: BankPayment): string[] {
  return words(`${p.who ?? ''} ${p.words}`).filter((w) => !NOISE.has(w))
}

// The payment's fingerprint (finding 4): the bank's own transaction id when
// the file has one, else the date, the amount and only the words the bank
// wrote. `occurrence` tells two identical payments on one day apart (finding 8).
export function paymentRef(p: BankPayment, occurrence = 1): string {
  const base = p.txid ? `id:${p.txid}` : `${p.date}|${p.pence}|${words(`${p.who ?? ''} ${p.words}`).join(' ').slice(0, 120)}`
  return occurrence > 1 ? `${base}#${occurrence}` : base
}

// The name to remember from a payment: the bank's name column when there is
// one, else the payment's words without the bank's own, numbers and dates.
// Empty when the payment comes through a till, a card company or cash.
export function payerNameOf(p: BankPayment): string {
  const all = words(p.who ?? p.words)
  const text = all.join(' ')
  if (NOT_A_PAYER.some((re) => re.test(text))) return ''
  // Only the name: the first words before any number, month or street word
  // ("MITCHELL S SEPT" remembers "MITCHELL S").
  const out: string[] = []
  for (const w of all) {
    if (NOISE.has(w)) continue
    if (/\d/.test(w) || MONTHS.has(w) || STREET_TYPES[w]) break
    out.push(w)
    if (out.length === 3) break
  }
  return out.join(' ')
}

interface NameFit {
  score: number
  reasons: string[]
  sureable: boolean
  rememberable: boolean
}

function nameFit(c: PayerCustomer, pw: string[], pwSet: Set<string>, pwText: string, firstNames: Set<string>): NameFit {
  const reasons: string[] = []
  let score = 0
  let sureable = false
  let rememberable = false
  // Remembered payer names (two words or more): the strongest sign there is.
  // Compared without the bank's own words on either side.
  for (const n of (c.payer_names ?? '').split('\n').map((x) => words(x).filter((w) => !NOISE.has(w)).join(' ')).filter((x) => x.split(' ').length >= 2)) {
    if (` ${pwText} `.includes(` ${n} `)) {
      score += 3
      reasons.push('remembered')
      sureable = true
      break
    }
  }
  // The customer's name, without titles, numbers or street words (finding 11).
  const nameWords = words(c.name.replace(/\(.*?\)/g, ' ')).filter((w) => !TITLES.has(w) && !/\d/.test(w) && !NOT_NAMES.has(w))
  const first = nameWords[0]
  const last = nameWords.length > 1 ? nameWords[nameWords.length - 1] : null
  if (last && last.length >= 3 && pwSet.has(last)) {
    // The words either side of the surname on the payment: a first name or
    // an initial there must be theirs (finding 1: "BEN SMITH" is not Anna Smith).
    const at = pw.indexOf(last)
    const near = [pw[at - 2], pw[at - 1], pw[at + 1], pw[at + 2]].filter((w): w is string => !!w && /^[A-Z]+$/.test(w) && w !== last)
    const firstFits = !!first && near.some((w) => w === first || (w.length === 1 && w === first[0]))
    // Only an initial or a first name someone on the round has can say it
    // is somebody else; a month or a street word next to the surname cannot
    // (round 2, finding 2).
    const contradicted = !firstFits && near.some((w) => w.length === 1 || firstNames.has(w))
    if (firstFits) {
      score += 2.5
      reasons.push('full name')
      sureable = true
      rememberable = true
    } else if (!contradicted) {
      score += 1.5
      reasons.push('surname')
    } else {
      // Same surname, different first name: offered for the owner to check, never sure.
      score += 0.5
      reasons.push('surname, other first name')
    }
  } else if (first && first.length >= 3 && pwSet.has(first) && !last) {
    // Known only by first name ("Yvonne"): a hint, never sure on its own (finding 13).
    score += 1
    reasons.push('first name')
  }
  // A name in brackets ("Zahda (saeed)") is often who pays: a hint only.
  const bracketed = words(c.name.match(/\(([^)]*)\)/)?.[1] ?? '').filter((w) => w.length >= 3)
  if (bracketed.some((b) => pwSet.has(b))) {
    score += 1
    reasons.push('name in brackets')
  }
  // Their house number right before their street name, and the street type
  // the same when the payment gives one (finding 12).
  // The house number is the one right before the street name, so "Flat 2,
  // 14 Oak Road" is number 14 (round 2, finding 8).
  const addr = words(c.address ?? '')
  const streetAt = addr.findIndex((w, i) => i > 0 && /^\d+[A-Z]?$/.test(addr[i - 1]) && w.length >= 3 && /^[A-Z]+$/.test(w) && !STREET_TYPES[w] && !NOT_NAMES.has(w))
  if (streetAt > 0) {
    const houseNo = addr[streetAt - 1]
    const streetName = addr[streetAt]
    const streetType = addr.slice(streetAt + 1).map((w) => STREET_TYPES[w]).find(Boolean)
    for (let i = 0; i < pw.length - 1; i++) {
      if (pw[i] !== houseNo || pw[i + 1] !== streetName) continue
      const theirType = pw[i + 2] ? STREET_TYPES[pw[i + 2]] : undefined
      if (theirType && streetType && theirType !== streetType) continue
      score += 2
      reasons.push('house and street')
      sureable = true
      break
    }
  }
  const pc = (c.postcode ?? '').toUpperCase().replace(/\s+/g, '')
  if (pc.length >= 5 && pwText.replace(/\s+/g, '').includes(pc)) {
    score += 2
    reasons.push('postcode')
    sureable = true
  }
  return { score, reasons, sureable, rememberable }
}

// Visits of one customer that add up to exactly the payment: one visit
// (unpaid before unrecorded, then oldest first: finding 6), or up to four
// together (someone paying for several cleans at once).
function visitsFor(pence: number, visits: OwedVisit[]): OwedVisit[] | null {
  const vs = [...visits].sort((a, b) => (a.status === b.status ? 0 : a.status === 'unpaid' ? -1 : 1) || a.date.localeCompare(b.date) || a.cleanId - b.cleanId)
  for (const v of vs) if (v.pence === pence) return [v]
  const byDate = [...visits].sort((a, b) => a.date.localeCompare(b.date) || a.cleanId - b.cleanId)
  const n = Math.min(byDate.length, 12)
  let best: OwedVisit[] | null = null
  const walk = (start: number, chosen: OwedVisit[], sum: number): void => {
    if (best) return
    if (sum === pence && chosen.length >= 2) {
      best = [...chosen]
      return
    }
    if (chosen.length === 4 || sum >= pence) return
    for (let i = start; i < n; i++) walk(i + 1, [...chosen, byDate[i]], sum + byDate[i].pence)
  }
  walk(0, [], 0)
  return best
}

// Every set of the customer's owed visits that adds up to exactly the payment, told apart by WHICH visits (several
// jobs, PLAN 4; Astra r1 findings 2 and 3): £60 is the gutters, or three windows visits, or a different three. All
// of them are searched (any number of visits) when the customer owes 12 or fewer; with more, the search cannot rule
// another set out, and `complete` says so. Stops after `limit` sets.
export const ALLOCATION_SEARCH_MAX = 12
// Quick on a big statement (Astra r2, finding 12): a total no set can reach is refused before searching, every branch
// that can no longer reach the payment (all the rest added together is too little) or has passed it is cut, and a
// search already made for the same visits and amount is reused (`memo`, one per statement).
export function allocationsFor(pence: number, visits: OwedVisit[], limit = 6, memo?: Map<string, { sets: OwedVisit[][]; complete: boolean }>): { sets: OwedVisit[][]; complete: boolean } {
  if (visits.length > ALLOCATION_SEARCH_MAX) return { sets: [], complete: false }
  const byDate = [...visits].sort((a, b) => a.date.localeCompare(b.date) || a.cleanId - b.cleanId)
  const key = memo ? `${pence}|${limit}|${byDate.map((v) => `${v.cleanId}:${v.pence}`).join(',')}` : ''
  const seen = memo?.get(key)
  if (seen) return seen
  const total = byDate.reduce((s, v) => s + v.pence, 0)
  const smallest = byDate.reduce((m, v) => Math.min(m, v.pence), Infinity)
  const sets: OwedVisit[][] = []
  if (pence > 0 && pence <= total && pence >= smallest) {
    // What every visit from i on adds up to, so a branch that cannot reach the payment stops at once.
    const rest: number[] = new Array(byDate.length + 1).fill(0)
    for (let i = byDate.length - 1; i >= 0; i--) rest[i] = rest[i + 1]! + byDate[i]!.pence
    const walk = (start: number, chosen: OwedVisit[], sum: number): void => {
      if (sets.length >= limit) return
      if (sum === pence && chosen.length >= 1) {
        sets.push([...chosen])
        return
      }
      if (sum > pence || sum + rest[start]! < pence) return
      for (let i = start; i < byDate.length; i++) {
        if (sum + byDate[i]!.pence > pence) continue
        walk(i + 1, [...chosen, byDate[i]!], sum + byDate[i]!.pence)
      }
    }
    walk(0, [], 0)
  }
  const out = { sets, complete: true }
  memo?.set(key, out)
  return out
}

const addDays = (iso: string, n: number): string => {
  const d = new Date(`${iso}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

export function matchPayments(payments: BankPayment[], owed: OwedVisit[], customers: PayerCustomer[], usedRefs: Set<string> = new Set(), paid: PaidVisit[] = [], options: MatchOptions = {}): MatchResult {
  const owing = new Map<number, OwedVisit[]>()
  for (const v of owed) {
    if (!(v.pence > 0)) continue
    const list = owing.get(v.customerId) ?? []
    list.push(v)
    owing.set(v.customerId, list)
  }
  const taken = new Set<number>()
  const result: MatchResult = { sure: [], check: [], unmatched: [], alreadyUsed: 0, alreadyPaid: [] }
  const memo = new Map<string, { sets: OwedVisit[][]; complete: boolean }>()
  const paidBy = new Map<number, PaidVisit[]>()
  for (const v of paid) {
    if (!(v.pence > 0)) continue
    const list = paidBy.get(v.customerId) ?? []
    list.push(v)
    paidBy.set(v.customerId, list)
  }
  const paidTaken = new Set<number>()
  const sorted = [...payments].sort((a, b) => a.date.localeCompare(b.date) || a.line - b.line)
  const seen = new Map<string, number>()

  // Every customer's name fit for each payment (finding 1: rivals are found
  // among ALL customers, not only those the amount fits).
  const firstNames = new Set(customers.map((c) => words(c.name.replace(/\(.*?\)/g, ' ')).filter((w) => !TITLES.has(w) && !/\d/.test(w) && !NOT_NAMES.has(w))[0]).filter((w): w is string => !!w && w.length >= 2))
  const prepared = sorted.map((p) => {
    const base = paymentRef(p)
    const occurrence = (seen.get(base) ?? 0) + 1
    seen.set(base, occurrence)
    const pw = paymentWords(p)
    const pwSet = new Set(pw)
    const pwText = pw.join(' ')
    const fits = customers.map((c) => ({ c, fit: nameFit(c, pw, pwSet, pwText, firstNames) }))
    return { p, ref: paymentRef(p, occurrence), fits }
  })

  // The candidates for a payment, against the visits not yet spoken for
  // (finding 7).
  const candidatesFor = (s: (typeof prepared)[number]): { named: Candidate[]; amountOnly: Candidate[] } => {
    const named: Candidate[] = []
    const amountOnly: Candidate[] = []
    for (const { c, fit } of s.fits) {
      const visits = (owing.get(c.id) ?? []).filter((v) => !taken.has(v.cleanId) && v.date <= addDays(s.p.date, 1))
      let fitVisits = visitsFor(s.p.pence, visits)
      // Several jobs on: every set of THEIR visits that adds up, by which visits. More than one, or more owed than
      // the search can rule out, is never sure: the owner ticks which visits it was for (Astra r1, findings 2, 3).
      // Off, the matching is exactly as it was.
      let unsure = false
      let alternatives: OwedVisit[][] | undefined
      let byHand = false
      if (options.severalJobs === true) {
        const found = allocationsFor(s.p.pence, visits, 6, memo)
        if (!fitVisits && found.sets.length) fitVisits = found.sets[0]!
        unsure = !found.complete || found.sets.length > 1
        if (unsure) alternatives = found.sets
        // More owed than the search reads and nothing found by it, but the payment names them (Astra r2, finding 9):
        // their visits are offered to tick by hand, never "Not matched".
        if (!fitVisits && !found.complete && fit.score > 0) {
          fitVisits = []
          byHand = true
        }
      }
      if (!fitVisits) continue
      const cand: Candidate = { customerId: c.id, name: c.name, visits: fitVisits, reasons: fit.reasons, score: fit.score, sureable: fit.sureable && !unsure, rememberable: fit.rememberable }
      if (unsure || byHand) {
        cand.alternatives = alternatives ?? []
        cand.owed = [...visits].sort((a, b) => a.date.localeCompare(b.date) || a.cleanId - b.cleanId)
      }
      if (fit.score > 0) named.push(cand)
      else amountOnly.push(cand)
    }
    named.sort((a, b) => b.score - a.score)
    return { named, amountOnly }
  }

  const pending: typeof prepared = []
  const byInvoice = new Map<(typeof prepared)[number], Candidate>()
  for (const s of prepared) {
    if (usedRefs.has(s.ref)) {
      result.alreadyUsed++
      continue
    }
    const inv = invoiceNamed(s.p, options.invoices)
    if (inv) {
      const visits = (owing.get(inv.customerId) ?? []).filter((v) => inv.cleanIds.includes(v.cleanId) && !taken.has(v.cleanId))
      if (visits.length) {
        const cand: Candidate = { customerId: inv.customerId, name: customers.find((c) => c.id === inv.customerId)?.name ?? '', visits, reasons: [`invoice ${inv.number}`], score: 100, sureable: true, rememberable: false }
        if (visits.reduce((n, v) => n + v.pence, 0) === s.p.pence) {
          for (const v of visits) taken.add(v.cleanId)
          result.sure.push({ payment: s.p, ref: s.ref, sure: cand, choices: [], hint: null })
          continue
        }
        byInvoice.set(s, { ...cand, sureable: false })
      }
    }
    const { named } = candidatesFor(s)
    const top = named[0]
    // Sure: strong evidence for them, and nobody else's name fits as well,
    // whether or not the amount fits them.
    // Round 2, findings 1 and 4: anyone else whose name is on the payment
    // (a surname, a one-word name, a remembered name, a full name) is a
    // rival, whatever their score.
    const NAME_REASONS = ['full name', 'surname', 'first name', 'remembered', 'name in brackets']
    const rivals = top ? s.fits.filter(({ c, fit }) => c.id !== top.customerId && (fit.score >= top.score || fit.reasons.some((r) => NAME_REASONS.includes(r)))).length : 0
    if (top && top.sureable && rivals === 0) {
      for (const v of top.visits) taken.add(v.cleanId)
      result.sure.push({ payment: s.p, ref: s.ref, sure: top, choices: [], hint: null })
    } else {
      pending.push(s)
    }
  }
  for (const s of pending) {
    const { named, amountOnly } = candidatesFor(s)
    // Every customer whose name is on it, then up to five who only fit the amount (round 2, finding 2).
    const named0 = [...named.slice(0, 8), ...amountOnly.sort((a, b) => a.visits[0].date.localeCompare(b.visits[0].date)).slice(0, Math.max(2, 5 - named.length))]
    const invoiceChoice = byInvoice.get(s)
    const choices = invoiceChoice ? [invoiceChoice, ...named0.filter((c) => c.customerId !== invoiceChoice.customerId)] : named0
    if (choices.length > 0) {
      result.check.push({ payment: s.p, ref: s.ref, sure: null, choices, hint: null })
      continue
    }
    // Nothing owed fits. Already paid? Only for the customer the payment
    // clearly names: a full name, a remembered payer name, their house and
    // street or their postcode (a surname alone could be anyone: Codex review
    // 2026-09-28), nobody else's name on it as strongly, and visits of exactly
    // that amount in the 120 days up to the payment, each counted once.
    // Anything less stays "Not matched".
    const ranked = s.fits.filter(({ fit }) => fit.score > 0 && !fit.reasons.includes('surname, other first name')).sort((a, b) => b.fit.score - a.fit.score)
    const lead = ranked[0] && ranked[0].fit.sureable ? ranked[0] : undefined
    const clear = !!lead && (ranked.length === 1 || ranked[1].fit.score < lead.fit.score)
    const paidFit = clear
      ? visitsFor(s.p.pence, (paidBy.get(lead.c.id) ?? [])
          .filter((v) => !paidTaken.has(v.cleanId) && v.date <= addDays(s.p.date, 1) && v.date >= addDays(s.p.date, -120))
          .map((v): OwedVisit => ({ cleanId: v.cleanId, customerId: v.customerId, date: v.date, pence: v.pence, version: 0, status: 'unpaid', chased: 0 })))
      : null
    const byId = new Map((paidBy.get(lead?.c.id ?? -1) ?? []).map((v) => [v.cleanId, v]))
    const paidVisits = paidFit ? paidFit.map((v) => byId.get(v.cleanId)!) : []
    if (lead && paidFit && paidVisits.some((v) => v.method === 'cash')) {
      // Money came by bank for a clean marked paid in cash: paid twice, or
      // marked the wrong way. Left for the owner to look at, never folded away.
      for (const v of paidFit) paidTaken.add(v.cleanId)
      const when = paidVisits.map((v) => v.date).join(', ')
      result.unmatched.push({ payment: s.p, ref: s.ref, sure: null, choices: [], hint: `Looks like ${lead.c.name}, but that clean (${when}) is marked paid in cash. Paid twice, or marked the wrong way?` })
    } else if (lead && paidFit) {
      for (const v of paidFit) paidTaken.add(v.cleanId)
      result.alreadyPaid.push({ payment: s.p, customerId: lead.c.id, name: lead.c.name, visits: paidVisits })
    } else {
      // What a named customer still owes, once this statement's sure matches are counted.
      const best = s.fits.filter(({ fit }) => fit.score >= 2).sort((a, b) => b.fit.score - a.fit.score)[0]
      let hint: string | null = null
      if (best) {
        const owes = (owing.get(best.c.id) ?? []).filter((v) => !taken.has(v.cleanId)).reduce((sum, v) => sum + v.pence, 0)
        hint = owes > 0 ? `Looks like ${best.c.name}, who owes £${(owes / 100).toFixed(2)}` : `Looks like ${best.c.name}, who owes nothing else`
      }
      result.unmatched.push({ payment: s.p, ref: s.ref, sure: null, choices: [], hint })
    }
  }
  return result
}

// The payer names to save for a customer after a confirmed match: the old
// list plus this one, newest first, at most 8, no repeats. One word
// ("SMITH") could be anyone in that family: only names of two words or more.
export function withPayerName(existing: string | null, name: string): string | null {
  const n = words(name).join(' ')
  if (n.length < 3 || n.split(' ').length < 2) return existing
  const list = (existing ?? '').split('\n').map((x) => words(x).join(' ')).filter(Boolean)
  if (list.includes(n)) return existing
  return [n, ...list].slice(0, 8).join('\n')
}
